/**
 * imageResourceAdapter.js — resource-registry adapter for Images
 * (Phase 6, see docs/PROJECT-RESOURCE-ARCHITECTURE-PLAN.md).
 *
 * Same interface shape as datasetResourceAdapter.js/fontResourceAdapter.js.
 * Unlike those two — which own fully in-memory, synchronously-available
 * Pinia state — imageStore.js persists to IndexedDB (src/utils/imageDB.js)
 * and its resources only exist in Pinia state once `load()`/`ensureLoaded()`
 * has resolved. This adapter does not change that: `list()` kicks off
 * `ensureLoaded()` without awaiting it and returns whatever's synchronously
 * available right now (empty on the very first call before load completes),
 * which is sufficient because ResourcePicker reads `list()` from inside a
 * Vue computed() — once the store's reactive `resources` array populates,
 * it re-renders on its own. No ResourcePicker changes were needed for this.
 *
 * imageStore's mutating actions (removeImage/duplicateImage/updateImage) key
 * off the numeric IndexedDB `id`, not the uuid every other adapter method
 * uses — this adapter resolves uuid -> resource -> id itself (see `resolve`
 * below) rather than changing imageStore's own method signatures.
 */

import { useImageStore } from '../imageStore.js'
import { isResourceUsed } from '../../utils/resourceUsage.js'
import { base64ToText } from '../../package/base64.js'

/** Shared uuid -> ImageResource lookup used by every mutating method below. */
function resolve(uuid) {
  return useImageStore().getResourceByUuid(uuid)
}

/**
 * Package entries always carry this adapter's JSON envelope as text, but a
 * source (GitHub blobs, in particular) may hand it back base64-encoded
 * regardless of its semantic content — decode before parsing either way.
 */
function parseEnvelope(entry) {
  const text = entry.encoding === 'base64' ? base64ToText(entry.content) : entry.content
  return JSON.parse(text)
}

export const imageResourceAdapter = {
  type: 'image',
  // Declares this adapter packageable and where its files live in a Design
  // Package (see docs plan "Design Packages") — the package layer only ever
  // checks for this field's presence, never branches on resource type itself.
  packageFolder: 'assets',

  list() {
    const store = useImageStore()
    store.ensureLoaded()
    return store.all
  },
  get(uuid) {
    return resolve(uuid)
  },
  remove(uuid) {
    const resource = resolve(uuid)
    if (!resource) return
    return useImageStore().removeImage(resource.id)
  },
  duplicate(uuid) {
    // imageStore.duplicateImage() doesn't surface the new image's id/uuid
    // (it awaits imageDB.copyImage() + a full reload, see imageStore.js) —
    // an existing limitation of the store this adapter deliberately doesn't
    // change. Callers can't chain off the return value the way they can for
    // dataset/font's duplicate().
    const resource = resolve(uuid)
    if (!resource) return null
    return useImageStore().duplicateImage(resource.id)
  },
  // `name` (not `filename`) to match the (uuid, name) shape every other
  // adapter's rename() uses — images just happen to treat `filename` as
  // their display name, same as getDisplayName() below.
  rename(uuid, name) {
    // updateImage() does a full IndexedDB `put()` (replace, not patch — see
    // imageDB.updateImageInDB), so the full existing row is spread here
    // rather than sending just `{ id, filename }`, which would otherwise
    // wipe labelId/src/imageSize.
    const resource = resolve(uuid)
    if (!resource) return
    return useImageStore().updateImage({ ...resource, filename: name })
  },
  isUsed(uuid, layer) {
    return isResourceUsed(layer, 'image', uuid)
  },
  getDisplayName(uuid) {
    return resolve(uuid)?.filename ?? null
  },

  // --- Packaging (Design Packages — see docs plan) ---

  /**
   * One image = one file in assets/: a JSON envelope (bytes + the resource's
   * original uuid), not raw binary. The uuid has to travel with the bytes:
   * widgets reference an image by uuid (`HaspImage._attachToUuid()` has no
   * filename fallback once `image` is set — unlike the legacy pre-uuid path),
   * and a cold import mints a brand-new uuid for the resource it creates, so
   * DesignPackageLoader needs the original uuid to remap widget references
   * (see originalUuid()/remapReference() below).
   */
  toPackageFile(uuid) {
    const resource = resolve(uuid)
    if (!resource?.src) return null
    const match = /^data:([^;]+);base64,(.*)$/s.exec(resource.src)
    if (!match) return null
    const [, mime] = match
    return {
      filename: `${resource.filename}.json`,
      encoding: 'utf8',
      mime: 'application/json',
      content: JSON.stringify({ uuid: resource.uuid, filename: resource.filename, mime, data: resource.src }),
    }
  },
  validatePackageFile(entry) {
    let parsed
    try {
      parsed = parseEnvelope(entry)
    } catch {
      throw new Error(`Image "${entry.filename}" is not valid`)
    }
    if (!parsed?.data || !/^data:/.test(parsed.data)) {
      throw new Error(`Image "${entry.filename}" has no image data`)
    }
  },
  async commitPackageFile(entry) {
    const store = useImageStore()
    await store.ensureLoaded()
    const parsed = parseEnvelope(entry)
    const existing = store.getResourceByFilename(parsed.filename)
    if (existing) return { uuid: existing.uuid, created: false }
    await store.addImage(parsed.data, parsed.filename)
    const created = store.getResourceByFilename(parsed.filename)
    return { uuid: created?.uuid ?? null, created: true }
  },
  /** The uuid this packaged file was exported under — see toPackageFile()'s comment. */
  originalUuid(entry) {
    try {
      return parseEnvelope(entry)?.uuid ?? null
    } catch {
      return null
    }
  },
  /** Rewrites widget `image` references so they still resolve after a cold import mints a new uuid. */
  remapReference(objects, oldUuid, newUuid) {
    for (const obj of objects) {
      if (obj.image === oldUuid) obj.image = newUuid
    }
  },
}
